import React, { useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { testimonials, heroImages } from "../mock";

export default function Testimonials() {
  const [active, setActive] = useState(0);

  const next = useCallback(() => setActive((v) => (v + 1) % testimonials.length), []);
  const prev = useCallback(() => setActive((v) => (v - 1 + testimonials.length) % testimonials.length), []);

  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <section id="temoignages" className="py-20 lg:py-28 bg-[#0e3a3f] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="blob w-[500px] h-[500px] bg-[#2c7a80] top-0 -left-32 opacity-70" />
        <div className="blob w-[400px] h-[400px] bg-[#e8a04d] bottom-0 -right-24 opacity-40" />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
        <div className="text-center mb-14">
          <span className="text-xs uppercase tracking-widest text-[#e8a04d] font-semibold">Ils nous ont fait confiance</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] mt-3 title-underline-center">
            La parole à nos<br />anciens stagiaires.
          </h2>
        </div>

        <div className="relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left: image */}
            <div className="lg:col-span-5 relative order-2 lg:order-1">
              <div className="aspect-square rounded-3xl overflow-hidden max-w-md mx-auto shadow-2xl">
                <img src={heroImages.drawing} alt="Témoignage" className="w-full h-full object-cover" style={{ objectPosition: "center 20%" }} />
              </div>
              <div className="absolute -top-6 -left-6 w-16 h-16 rounded-2xl bg-[#e8a04d] flex items-center justify-center shadow-xl">
                <Quote className="w-8 h-8 text-white" fill="currentColor" />
              </div>
            </div>

            {/* Right: content */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative min-h-[280px]">
                {testimonials.map((t, i) => (
                  <div
                    key={t.id}
                    className={`transition-all duration-700 absolute inset-0 ${
                      i === active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
                    }`}
                  >
                    <div className="flex gap-1 mb-5">
                      {Array.from({length: t.rating}).map((_, ri) => (
                        <Star key={`star-${ri}`} className="w-5 h-5 text-[#e8a04d]" fill="currentColor" />
                      ))}
                    </div>
                    <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight font-medium text-white/95 mb-8">
                      &ldquo;{t.content}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2c7a80] to-[#4ba9b0] flex items-center justify-center text-white font-semibold">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{t.name}</div>
                        <div className="text-sm text-white/60">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mt-4 pt-6 border-t border-white/10">
                <div className="flex gap-2">
                  {testimonials.map((t, i) => (
                    <button
                      key={t.id}
                      aria-label={`Témoignage ${i+1}`}
                      onClick={() => setActive(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === active ? "w-8 bg-[#e8a04d]" : "w-2 bg-white/30 hover:bg-white/50"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={prev}
                    aria-label="Précédent"
                    className="w-11 h-11 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Suivant"
                    className="w-11 h-11 rounded-full bg-[#e8a04d] hover:bg-[#d68a3a] flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
